import { FC, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

import { updateMyInfo } from "src/api/user";
import Button from "src/components/Button";
import useUpdateUser from "src/hooks/useUpdateUser";
import useUser from "src/hooks/useUser";
import { captureHandledException } from "src/services/sentry";

/** `users.fcl` is a VARCHAR(191). */
export const FCL_MAX_LENGTH = 191;

export interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

/** The user's own settings that live in CO. For now only the FCL (flight crew licence) number. */
const SettingsDialog: FC<SettingsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const user = useUser() as { fcl?: string | null } | null;
  const updateUser = useUpdateUser();
  const [fcl, setFcl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the dialog opens so a cancelled edit does not leak into the next one.
  useEffect(() => {
    if (open) {
      setFcl(user?.fcl ?? "");
      setError(null);
    }
  }, [open, user?.fcl]);

  const tooLong = fcl.trim().length > FCL_MAX_LENGTH;

  const handleSave = async () => {
    const value = fcl.trim() || null;
    setSaving(true);
    setError(null);
    try {
      await updateMyInfo({ fcl: value });
      updateUser({ fcl: value });
      enqueueSnackbar(t("header_settings.saved"), { variant: "success" });
      onClose();
    } catch (saveError) {
      captureHandledException(saveError, { component: "SettingsDialog", action: "saveFcl" });
      setError(t("header_settings.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("header_settings.settings")}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t("header_settings.fcl")}
          value={fcl}
          onChange={(e) => setFcl(e.target.value)}
          disabled={saving}
          error={tooLong}
          helperText={tooLong ? t("header_settings.fcl_too_long", { max: FCL_MAX_LENGTH }) : undefined}
          fullWidth
          autoFocus
          margin="dense"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button color="primary" disabled={saving || tooLong} onClick={handleSave}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
